import { App, createApp } from "vue"

import "@/assets/scss/app.scss"
import "@/assets/scss/app.dark.scss"
import "@/models"

import { Fancybox } from "@fancyapps/ui/src/Fancybox/Fancybox.js"
import { plugin as unleashPlugin } from "@unleash/proxy-client-vue"

import BaseComponents from "@/components/Base"

import { configureServerSentEvent } from "@/events/index"
import { AlertType } from "@/models/Alert"
import acl from "@/plugins/acl"
import { $t, $tc, applyLocalization } from "@/plugins/i18n"
import { unleashClient } from "@/plugins/unleash"
import waitPlugin from "@/plugins/vuewait"
import { applyMiddleware, router } from "@/router"
import { Getters, store } from "@/store"
import { AlertActions, alertModule } from "@/store/modules/alert.module"
import { setActiveTimeZoneProvider } from "@/utils/dateUtil"

import { config, isLocalHost } from "./app.config"
import AppComponent from "./App.vue"
import { applyInterceptors } from "./plugins/axios"
import { $fancybox } from "./plugins/fancybox"
import { $keycloak, KEYCLOAK_AUTH_SYNC_KEY, logout } from "./plugins/keycloak"
import { pathSignUp } from "./router/routers"
import { decoratorService } from "./services/decorator.service"
import { errorHandlerService } from "./services/error-handler.service"
import { trackService } from "./services/track.service"
import { LogActions, logModule } from "./store/modules/log.module"

const isSignUp = window.location.pathname.includes(pathSignUp)
const invitationMatch = window.location.pathname.match(/\/invitation\/([^/]+)/)
const isWrongInvitee = window.location.pathname.includes("/wrong-invitee-error")

declare module "@vue/runtime-core" {
  export interface ComponentCustomProperties {
    // eslint-disable-next-line no-undef
    $context: AppContext
    $fancybox: Fancybox
  }
}

declare global {
  interface Window {
    globalConnectionErrorShown: boolean
    documentPictureInPicture?: {
      requestWindow(options?: {
        width?: number
        height?: number
        disallowReturnToOpener?: boolean
      }): Promise<Window>
    }
  }
}

export class Main {
  app: App<Element>

  constructor() {
    this.bootstrap()
    if (!isLocalHost()) {
      errorHandlerService.init(logModule[LogActions.LOG_ERROR])
      trackService.init(logModule[LogActions.LOG_PAGE_LOAD])
    }

    window.globalConnectionErrorShown = false

    const resetErrorFlag = () => {
      setTimeout(() => {
        window.globalConnectionErrorShown = false
      }, 5000)
    }

    const showConnectionError = () => {
      if (window.globalConnectionErrorShown) return
      window.globalConnectionErrorShown = true
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.ERROR,
        contentTitle: [{ text: "Connection timed out ", type: "bold" }],
        content: [{ text: "Please refresh", type: "regular" }],
        theme: "global",
        hideDetails: false
      })

      resetErrorFlag()
    }
    const isNetworkError = (e: Error | any): boolean => {
      const errorMessages = [
        "net::ERR",
        "ERR_INTERNET_DISCONNECTED",
        "ChunkLoadError",
        "Loading chunk failed"
      ]
      return (
        e.code === "ECONNABORTED" ||
        (e?.message && errorMessages.some(msg => e.message.includes(msg)))
      )
    }

    window.addEventListener(
      "error",
      event => {
        if (event.target instanceof HTMLScriptElement || event.target instanceof HTMLLinkElement) {
          showConnectionError()
          return
        }

        if (isNetworkError(event.error || event)) {
          showConnectionError()
        }
      },
      true
    )

    window.addEventListener("unhandledrejection", event => {
      if (isNetworkError(event.reason || event)) {
        showConnectionError()
      }
    })
  }

  private async keyclockInit(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      $keycloak
        .init({
          onLoad: isSignUp || invitationMatch ? "check-sso" : "login-required",
          checkLoginIframe: false
        })
        .then(auth => {
          if (auth && !localStorage.getItem(KEYCLOAK_AUTH_SYNC_KEY)) {
            localStorage.setItem(KEYCLOAK_AUTH_SYNC_KEY, Date.now().toString())
          }
          setTimeout(() => {
            localStorage.removeItem(KEYCLOAK_AUTH_SYNC_KEY)
          }, 1000)
          return resolve(auth)
        })
        .catch(err => {
          console.error("Keycloak init error", err)
          resolve(false)
        })
    })
  }

  private async bootstrap(): Promise<void> {
    const auth = await this.keyclockInit()
    if (isSignUp) {
      const signUpUrl = new URL(window.location.href)
      const planId = signUpUrl.searchParams.get("planId")
      const period = signUpUrl.searchParams.get("planInterval")
      if (planId) {
        sessionStorage.setItem("planId", planId)
      }
      if (period) {
        sessionStorage.setItem("period", period)
      }
      sessionStorage.setItem("toSignUp", "true")
      logout()
      return
    }
    if (invitationMatch) {
      logout(`${config.VUE_APP_API_LOCATION}/api/v1/invitation/${invitationMatch[1]}/redirect`)
      return
    }
    const sse = () => configureServerSentEvent()
    this.app = createApp(AppComponent)

    this.app.use(unleashPlugin, { unleashClient })
    this.app.use(waitPlugin)
    this.app.use(store)
    this.app.use(router)
    this.app.use(acl)

    applyLocalization(this)

    this.registerBaseComponents()

    await applyMiddleware()
    await applyInterceptors()

    if (!auth) {
      window.location.reload()
    }

    await this.postInit()

    this.setInjects()

    this.app.mount("#app")
    if (isWrongInvitee) {
      return
    }

    sse()
  }

  setInjects() {
    //   this.app.provide('updateToken', updateToken.bind(null, $keycloak));
  }

  applyConfig() {
    // this.app.config.productionTip = false;
    this.app.config.globalProperties.$t = $t
    this.app.config.globalProperties.$tc = $tc
    this.app.config.globalProperties.$context = this.app._context
    this.app.config.globalProperties.$fancybox = $fancybox
    this.app.config.errorHandler = function (err: any, vm, info) {
      errorHandlerService.onAppError(err, info)
    }
  }

  registerBaseComponents() {
    BaseComponents.register(this.app)
  }

  async postInit() {
    this.applyConfig()
    decoratorService.setContext(this.app._context)
    decoratorService.setStore(store)
    setActiveTimeZoneProvider(() => store.getters[Getters.GET_USER_CURRENT_PROFILE]?.timeZone)
    await import("@/plugins")
  }
}

export const main = new Main()
